import React, { RefObject, useEffect } from "react";
import { getTextProps } from "../../utils";

interface VideoProps {
  videoSrc: {
    cloudinaryVideo: { src: string; breakpoint: number; poster: string }[];
    videoName?: string;
    videoDescription?: string;
    altText?: string;
    posterImageFrame?: string | { cloudinaryImage: string };
  };
  videoRef: RefObject<HTMLVideoElement | null>;
}

function getVideoStructuredData(name: string, description: string) {
  return JSON.stringify({
    "@context": "https://schema.org",
    "@type": "VideoObject",
    name,
    description,
  });
}

const Video: React.FC<VideoProps> = ({ videoSrc, videoRef }) => {
  const sortedVideos = [...videoSrc.cloudinaryVideo].sort((a, b) => b.breakpoint - a.breakpoint);

  const defaultPoster = videoSrc.posterImageFrame
    ? typeof videoSrc.posterImageFrame === "string"
      ? videoSrc.posterImageFrame
      : videoSrc.posterImageFrame.cloudinaryImage
    : sortedVideos[0]?.poster;

  useEffect(() => {
    const script = document.createElement("script");
    script.type = "application/ld+json";

    if (videoSrc.videoName && videoSrc.videoDescription) {
      const processedVideoName =
        typeof videoSrc.videoName === "string"
          ? videoSrc.videoName
          : (getTextProps(videoSrc.videoName) ?? "");

      const processedVideoDescription =
        typeof videoSrc.videoDescription === "string"
          ? videoSrc.videoDescription
          : (getTextProps(videoSrc.videoDescription) ?? "");

      script.textContent = getVideoStructuredData(processedVideoName, processedVideoDescription);
    }
    document.head.appendChild(script);

    return () => {
      document.head.removeChild(script);
    };
  }, []);

  return (
    <video
      loop={false}
      autoPlay={false}
      muted
      ref={videoRef as React.RefObject<HTMLVideoElement>}
      data-testid='banner-video'
      style={{ width: "100%", height: "auto" }}
      poster={defaultPoster}
      className='banner-video'
      aria-label={videoSrc?.altText && getTextProps(videoSrc?.altText)}
    >
      {sortedVideos.map((video) => (
        <source
          key={video.breakpoint}
          src={video.src}
          media={`(min-width: ${video.breakpoint}px)`}
          type='video/mp4'
        />
      ))}
      {sortedVideos.length > 0 && (
        <source
          src={sortedVideos[sortedVideos.length - 1].src}
          media='(max-width: 767px)'
          type='video/mp4'
        />
      )}
    </video>
  );
};

export default Video;
